import * as THREE from 'three';

/**
 * Utility class for scaling 3D models to maintain real-world scale
 * Target: 1 cm in real life = 1 unit in Three.js scene
 */

// Unit conversion factors to centimeters (target unit)
export const UNIT_SCALES = {
  mm: 0.1,      // 1 mm = 0.1 cm
  cm: 1.0,      // 1 cm = 1 cm
  m: 100.0,     // 1 m = 100 cm
  inches: 2.54, // 1 inch = 2.54 cm
  auto: null,   // Auto-detect
};

/**
 * Calculate the bounding box of a mesh
 * @param {THREE.Mesh} mesh - The mesh to measure
 * @returns {THREE.Box3} The bounding box
 */
export function calculateBoundingBox(mesh) {
  const boundingBox = new THREE.Box3().setFromObject(mesh);
  return boundingBox;
}

/**
 * Get dimensions from a bounding box
 * @param {THREE.Box3} boundingBox
 * @returns {{width: number, height: number, depth: number, max: number}}
 */
export function getBoundingBoxDimensions(boundingBox) {
  const size = new THREE.Vector3();
  boundingBox.getSize(size);

  return {
    width: size.x,
    height: size.y,
    depth: size.z,
    max: Math.max(size.x, size.y, size.z),
  };
}

/**
 * Detect the likely unit of a model based on its bounding box dimensions
 * Assumes prosthetic sockets have a typical size range of 10-40 cm in largest dimension
 *
 * @param {THREE.Box3} boundingBox - The model's bounding box
 * @returns {{detectedUnit: string, confidence: string, scaleFactor: number}}
 */
export function detectUnits(boundingBox) {
  const dimensions = getBoundingBoxDimensions(boundingBox);
  const largestDim = dimensions.max;

  // Heuristic detection based on typical prosthetic socket sizes (10-40 cm)
  if (largestDim > 500) {
    // Likely in millimeters (e.g., 100-400mm for a socket)
    return {
      detectedUnit: 'mm',
      confidence: 'high',
      scaleFactor: UNIT_SCALES.mm,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) > 500 units suggests millimeters`,
    };
  } else if (largestDim >= 50 && largestDim <= 500) {
    // Likely in centimeters (e.g., 10-40cm for a socket)
    return {
      detectedUnit: 'cm',
      confidence: 'high',
      scaleFactor: UNIT_SCALES.cm,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) in 50-500 range suggests centimeters`,
    };
  } else if (largestDim >= 5 && largestDim < 50) {
    // Ambiguous - could be large cm model or small meter model
    // Default to cm for prosthetic sockets
    return {
      detectedUnit: 'cm',
      confidence: 'medium',
      scaleFactor: UNIT_SCALES.cm,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) in 5-50 range is ambiguous, defaulting to centimeters`,
    };
  } else if (largestDim < 5) {
    // Likely in meters (e.g., 0.1-0.4m for a socket)
    return {
      detectedUnit: 'm',
      confidence: 'high',
      scaleFactor: UNIT_SCALES.m,
      reasoning: `Largest dimension (${largestDim.toFixed(1)}) < 5 units suggests meters`,
    };
  }

  // Fallback to cm
  return {
    detectedUnit: 'cm',
    confidence: 'low',
    scaleFactor: UNIT_SCALES.cm,
    reasoning: 'Could not determine units reliably, defaulting to centimeters',
  };
}

/**
 * Apply scaling to a mesh based on source and target units
 * IMPORTANT: Applies transformation directly to geometry (bakes it)
 * This ensures slicing and other geometry operations work correctly
 *
 * @param {THREE.Mesh} mesh - The mesh to scale
 * @param {string} sourceUnit - The unit the model is currently in ('mm', 'cm', 'm', 'inches')
 * @param {string} targetUnit - The target unit (default: 'cm')
 * @returns {{scaleFactor: number, dimensions: object}}
 */
export function applyScaleForUnit(mesh, sourceUnit, targetUnit = 'cm') {
  const sourceScale = UNIT_SCALES[sourceUnit];
  const targetScale = UNIT_SCALES[targetUnit];

  if (!sourceScale || !targetScale) {
    console.warn(`Invalid unit specified: ${sourceUnit} -> ${targetUnit}`);
    return { scaleFactor: 1.0, dimensions: getBoundingBoxDimensions(calculateBoundingBox(mesh)) };
  }

  // Calculate scale factor to convert from source to target
  // Example: mm to cm = 0.1, m to cm = 100
  const scaleFactor = sourceScale / targetScale;

  // CRITICAL: Apply scaling directly to geometry, not to mesh transform
  // This bakes the transformation into vertex data
  mesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);

  // Update bounding sphere for correct culling
  mesh.geometry.computeBoundingSphere();

  // Recalculate dimensions after scaling
  const scaledBox = calculateBoundingBox(mesh);
  const dimensions = getBoundingBoxDimensions(scaledBox);

  return { scaleFactor, dimensions };
}

/**
 * Apply a manual scale factor to a mesh
 * IMPORTANT: Applies transformation directly to geometry (bakes it)
 *
 * @param {THREE.Mesh} mesh - The mesh to scale
 * @param {number} scaleFactor - The scale factor to apply
 * @returns {{dimensions: object}}
 */
export function applyManualScale(mesh, scaleFactor) {
  // CRITICAL: Apply scaling directly to geometry, not to mesh transform
  mesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);

  // Update bounding sphere for correct culling
  mesh.geometry.computeBoundingSphere();

  const scaledBox = calculateBoundingBox(mesh);
  const dimensions = getBoundingBoxDimensions(scaledBox);

  return { dimensions };
}

/**
 * Center a mesh at the origin
 * IMPORTANT: Applies transformation directly to geometry (bakes it)
 *
 * @param {THREE.Mesh} mesh - The mesh to center
 */
export function centerModel(mesh) {
  const boundingBox = calculateBoundingBox(mesh);
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  // CRITICAL: Translate geometry vertices directly, not mesh position
  // This bakes the transformation into vertex data
  mesh.geometry.translate(-center.x, -center.y, -center.z);

  // Update bounding sphere for correct culling
  mesh.geometry.computeBoundingSphere();
}

/**
 * Complete model scaling pipeline
 * @param {THREE.Mesh} mesh - The mesh to process
 * @param {string|null} manualUnit - Manual unit override, or null for auto-detect
 * @param {number} manualScaleFactor - Additional manual scale adjustment (default: 1.0)
 * @returns {{detectedUnit: string, confidence: string, scaleFactor: number, dimensions: object, reasoning: string}}
 */
export function scaleModelToCentimeters(mesh, manualUnit = null, manualScaleFactor = 1.0) {
  // Step 1: Calculate original bounding box
  const originalBox = calculateBoundingBox(mesh);
  const originalDimensions = getBoundingBoxDimensions(originalBox);

  console.log('📏 Original model dimensions:', originalDimensions);

  // Step 2: Detect or use manual unit
  let unitInfo;
  if (manualUnit && manualUnit !== 'auto' && UNIT_SCALES[manualUnit] !== undefined) {
    // Manual unit specified
    unitInfo = {
      detectedUnit: manualUnit,
      confidence: 'manual',
      scaleFactor: UNIT_SCALES[manualUnit],
      reasoning: `Manually specified unit: ${manualUnit}`,
    };
  } else {
    // Auto-detect
    unitInfo = detectUnits(originalBox);
  }

  console.log(`🔍 Unit detection: ${unitInfo.detectedUnit} (${unitInfo.confidence} confidence)`);
  console.log(`   Reasoning: ${unitInfo.reasoning}`);

  // Step 3: Apply unit scale
  const { scaleFactor: unitScaleFactor, dimensions: scaledDimensions } = applyScaleForUnit(
    mesh,
    unitInfo.detectedUnit,
    'cm'
  );

  console.log(`⚖️  Applied scale factor: ${unitScaleFactor.toFixed(3)}`);
  console.log('📏 Scaled dimensions (in cm):', scaledDimensions);

  // Step 4: Apply manual scale adjustment if needed
  if (manualScaleFactor !== 1.0) {
    // CRITICAL: Apply to geometry, not mesh transform
    applyManualScale(mesh, manualScaleFactor);
    console.log(`🔧 Applied additional manual scale: ${manualScaleFactor.toFixed(3)}`);
  }

  // Step 5: Center the model
  centerModel(mesh);
  console.log('✅ Model centered at origin');

  // Step 6: Get final dimensions
  const finalBox = calculateBoundingBox(mesh);
  const finalDimensions = getBoundingBoxDimensions(finalBox);

  return {
    detectedUnit: unitInfo.detectedUnit,
    confidence: unitInfo.confidence,
    scaleFactor: unitScaleFactor * manualScaleFactor,
    dimensions: finalDimensions,
    reasoning: unitInfo.reasoning,
    originalDimensions,
  };
}
